import React from 'react';
import { useState } from 'react';
import scansImage from './scans_00000.png';

export default function UltrasoundAnimation() {
  const [activeSweep, setActiveSweep] = useState<number | null>(null);

  const startSweep = (sweepNumber: number) => {
    setActiveSweep(sweepNumber);
    // Auto-stop after 5 seconds (animation duration)
    setTimeout(() => {
      setActiveSweep(null);
    }, 5000);
  };

  const stopSweep = () => {
    setActiveSweep(null);
  };

  const getSweepClass = () => {
    if (activeSweep === null) {
      return 'left-[20%] top-[50%]';
    }
    return `ultrasound-sweep-${activeSweep}`;
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto bg-gray-100 rounded-2xl p-6 shadow-lg">
      <div
        className="relative w-full h-[600px] bg-contain bg-center bg-no-repeat mx-auto"
        style={{
          backgroundImage: `url(${scansImage})`,
          backgroundSize: 'contain',
        }}
      >
        {/* L and R markers */}
        <div className="absolute left-[15%] top-[35%] text-blue-600 font-bold text-3xl drop-shadow-lg">
          L
        </div>
        <div className="absolute right-[15%] top-[35%] text-blue-600 font-bold text-3xl drop-shadow-lg">
          R
        </div>

        {/* Status indicator */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold">
          {activeSweep !== null
            ? `Blind Sweep ${activeSweep}`
            : 'Select a Sweep'}
        </div>

        {/* Ultrasound probe */}
        <div
          className={`absolute w-12 h-8 transition-all duration-5000 ease-in-out ${getSweepClass()}`}
        >
          {/* Probe body */}
          <div className="w-10 h-6 bg-blue-600 rounded-lg relative drop-shadow-md">
            {/* Probe handle */}
            <div className="w-3 h-8 bg-blue-700 rounded-t-lg absolute -top-2 left-1/2 -translate-x-1/2"></div>
            {/* Probe tip */}
            <div className="w-8 h-2 bg-blue-800 rounded-b-lg absolute -bottom-1 left-1/2 -translate-x-1/2"></div>
          </div>

          {/* Scanning waves - only show when actively scanning */}
          {activeSweep !== null && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
              <div className="w-8 h-1 bg-blue-400 rounded-full opacity-60 animate-pulse"></div>
              <div className="w-12 h-1 bg-blue-300 rounded-full opacity-40 animate-pulse animation-delay-200 mt-1"></div>
              <div className="w-16 h-1 bg-blue-200 rounded-full opacity-20 animate-pulse animation-delay-400 mt-1"></div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[1, 2, 3, 4, 5, 6, 7].map((sweepNum) => (
            <button
              key={sweepNum}
              onClick={() => startSweep(sweepNum)}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                activeSweep === sweepNum
                  ? 'bg-blue-700 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Sweep {sweepNum}
            </button>
          ))}
        </div>

        {/* Stop button */}
        <div className="text-center">
          <button
            onClick={stopSweep}
            disabled={activeSweep === null}
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Stop Scanning
          </button>
        </div>
      </div>

      <style>{`
        /* Modified animations to loop infinitely when active */

        /* Sweep 1: Horizontal across upper abdomen */
        .ultrasound-sweep-1 {
          animation: sweep1 5s ease-in-out infinite;
        }

        @keyframes sweep1 {
          0% {
            left: 25%;
            top: 45%;
            transform: rotate(0deg);
          }
          50% {
            left: 70%;
            top: 45%;
            transform: rotate(0deg);
          }
          100% {
            left: 25%;
            top: 45%;
            transform: rotate(0deg);
          }
        }

        /* Sweep 2: Vertical in center */
        .ultrasound-sweep-2 {
          animation: sweep2 5s ease-in-out infinite;
        }

        @keyframes sweep2 {
          0% {
            left: 48%;
            top: 40%;
            transform: rotate(90deg);
          }
          25% {
            left: 48%;
            top: 60%;
            transform: rotate(90deg);
          }
          50% {
            left: 48%;
            top: 80%;
            transform: rotate(90deg);
          }
          75% {
            left: 48%;
            top: 60%;
            transform: rotate(90deg);
          }
          100% {
            left: 48%;
            top: 40%;
            transform: rotate(90deg);
          }
        }

        /* Sweep 3: Vertical in center-left */
        .ultrasound-sweep-3 {
          animation: sweep3 5s ease-in-out infinite;
        }

        @keyframes sweep3 {
          0% {
            left: 30%;
            top: 40%;
            transform: rotate(90deg);
          }
          25% {
            left: 30%;
            top: 60%;
            transform: rotate(90deg);
          }
          50% {
            left: 30%;
            top: 80%;
            transform: rotate(90deg);
          }
          75% {
            left: 30%;
            top: 60%;
            transform: rotate(90deg);
          }
          100% {
            left: 30%;
            top: 40%;
            transform: rotate(90deg);
          }
        }

        /* Sweep 4: Vertical in center-right */
        .ultrasound-sweep-4 {
          animation: sweep4 5s ease-in-out infinite;
        }

        @keyframes sweep4 {
          0% {
            left: 65%;
            top: 40%;
            transform: rotate(90deg);
          }
          25% {
            left: 65%;
            top: 60%;
            transform: rotate(90deg);
          }
          50% {
            left: 65%;
            top: 80%;
            transform: rotate(90deg);
          }
          75% {
            left: 65%;
            top: 60%;
            transform: rotate(90deg);
          }
          100% {
            left: 65%;
            top: 40%;
            transform: rotate(90deg);
          }
        }

        /* Sweep 5: Horizontal across middle abdomen */
        .ultrasound-sweep-5 {
          animation: sweep5 5s ease-in-out infinite;
        }

        @keyframes sweep5 {
          0% {
            left: 25%;
            top: 55%;
            transform: rotate(-15deg);
          }
          25% {
            left: 40%;
            top: 45%;
            transform: rotate(-5deg);
          }
          50% {
            left: 70%;
            top: 45%;
            transform: rotate(15deg);
          }
          75% {
            left: 55%;
            top: 42%;
            transform: rotate(5deg);
          }
          100% {
            left: 25%;
            top: 55%;
            transform: rotate(-15deg);
          }
        }

        /* Sweep 6: Horizontal across lower abdomen */
        .ultrasound-sweep-6 {
          animation: sweep6 5s ease-in-out infinite;
        }

        @keyframes sweep6 {
          0% {
            left: 25%;
            top: 60%;
            transform: rotate(0deg);
          }
          25% {
            left: 40%;
            top: 60%;
            transform: rotate(0deg);
          }
          50% {
            left: 60%;
            top: 60%;
            transform: rotate(0deg);
          }
          75% {
            left: 75%;
            top: 60%;
            transform: rotate(0deg);
          }
          100% {
            left: 60%;
            top: 60%;
            transform: rotate(0deg);
          }
        }

        /* Sweep 7: Curved along bottom of belly */
        .ultrasound-sweep-7 {
          animation: sweep7 5s ease-in-out infinite;
        }

        @keyframes sweep7 {
          0% {
            left: 25%;
            top: 65%;
            transform: rotate(-30deg);
          }
          25% {
            left: 40%;
            top: 70%;
            transform: rotate(-15deg);
          }
          50% {
            left: 50%;
            top: 72%;
            transform: rotate(0deg);
          }
          75% {
            left: 60%;
            top: 70%;
            transform: rotate(15deg);
          }
          100% {
            left: 75%;
            top: 65%;
            transform: rotate(30deg);
          }
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .duration-5000 {
          transition-duration: 5s;
        }
      `}</style>
    </div>
  );
}
